import React, { useEffect } from 'react';
import { Routes, Route } from 'react-router-dom';
import AssetStatus from './features/asset-status/AssetStatus';
import Asset from './features/asset/components/Asset';
import GroupStatus from './features/group-status/GroupStatus';
import './App.css';
import { Layout } from './layouts/PageLayout';
import OnboardingShell from './onboarding/OnboardingShell';
// import { DefaultView } from './onboarding/default-view/DefaultView';
import AuthComponent from './features/common/AuthComponent';
import ServerError from './features/server-error/ServerError';
import ADLoginError from './features/adlogin-error/ADLoginError';
import LoginRefToken from './features/common/Login/LoginRefToken';
import { setFavicon } from './utilities/SetFavicon';
import Login from './features/common/Login/Login';
import { useLoginSettings } from './hooks/useSettingUrls';
import EspWellChart from './well-chart/WellChart';
import Downtime from './features/downtime/Downtime';
import WellTest from './test-well-screen/WellTest';
import WellAlarms from './components/well-alarms/WellAlarms';
import WellTasks from './components/tasks-list/wellTasks';
import { getDummyUserId } from './utilities/getDummyUserId';
import { AnalyticsListener } from './components/analytics/AnalyticsListener';
import { useErrorTracking } from './hooks/useErrorTracking';
import { userAnalytics } from './hooks/userAnalytics';
import { userClickAnalytics } from './hooks/userClickAnalytics';
import SetPointsManager from './features/setpoints-manager/SetPointsManager';
import CustomViews from './features/custom-views/CustomViews';
import WellLocations from './features/well-locations/WellLocations';
import Recommendations from './features/recommendations/Recommendations';
import { ToastServiceProvider } from './components/toast/ToastService';
import { PermissionModalProvider } from './features/common/PermissionModal/PermissionModalService';
import Installation from './features/installs/Installation';
import WellCompletions from './features/well-completions/WellCompletions';
import { RbacProvider } from './rbac';
import SummaryDashboard from './features/well-summary/SummaryDashboard';
import WellDiagnosis from './features/well-diagnosis/WellDiagnosis';
import GLDashboard from './features/gl-dashboard';
import ESPDashboard from './features/esp-dashboard/ESPDashboard';
import { RealtimeInsights } from './features/well-summary/RealtimeInsights';
import TooltipManager from './components/common/simple-tool-tip/TooltipManager';
import RunlifeDashboard from './features/runlife-dashboard/RunlifeDashboard';
import WellNotesTasks from './features/well-notes-tasks/WellNotesTasks';
import WellComments from './features/well-comments/WellComments';
import TokenService from './services/authentication/token.service';

const App: React.FC = () => {
  const { isCloudDeployment, isConnexiaLogin } = useLoginSettings();

  useEffect(() => {
    const mediaQuery = window.matchMedia('(prefers-color-scheme: dark)');
    const applyTheme = () => {
      setFavicon();
    };

    applyTheme();

    mediaQuery.addEventListener('change', applyTheme);

    return () => {
      mediaQuery.removeEventListener('change', applyTheme);
    };
  }, []);

  // Analytics initialization
  const userName = TokenService.getUser() || getDummyUserId();
  const { markPageLoaded } = userAnalytics(userName);
  userClickAnalytics(userName);
  useErrorTracking(userName);

  return (
    <RbacProvider>
      <ToastServiceProvider>
        <PermissionModalProvider>
          <AnalyticsListener markPageLoaded={markPageLoaded}>
            <div data-testid='router'>
              <Routes>
                <Route path='/login' element={isConnexiaLogin || !isCloudDeployment ? <LoginRefToken /> : <Login />} />
                <Route path='/' element={<AuthComponent component={Layout} />}>
                  <Route path='/' element={<AuthComponent component={SummaryDashboard} />} />
                  <Route path='/espDashboard' element={<AuthComponent component={ESPDashboard} />} />
                  <Route path='/asset/' element={<Asset />}>
                    <Route path='status/:assetId/:assetGroupName' element={<AuthComponent component={AssetStatus} />} />
                    {/* 73702: Disable analysis tab for all lift Types
                    <Route
                      path='analysis/:assetId/:assetGroupName'
                      element={<AuthComponent component={AssetAnalysis} />}
                    /> */}
                    <Route
                      path='charts/:assetId/:assetGroupName'
                      element={<AuthComponent component={EspWellChart} />}
                    />
                    <Route path='downtime/:assetId/:assetGroupName' element={<AuthComponent component={Downtime} />} />
                    <Route
                      path='notesAndTasks/:assetId/:assetGroupName'
                      element={<AuthComponent component={WellNotesTasks} />}
                    />
                    <Route
                      path='comments/:assetId/:assetGroupName'
                      element={<AuthComponent component={WellComments} />}
                    />
                    <Route path='alarms/:assetId/:assetGroupName' element={<AuthComponent component={WellAlarms} />} />
                    <Route path='tasks/:assetId/:assetGroupName' element={<AuthComponent component={WellTasks} />} />
                    <Route path='tests/:assetId/:assetGroupName' element={<AuthComponent component={WellTest} />} />
                    <Route
                      path='completions/:assetId/:assetGroupName'
                      element={<AuthComponent component={WellCompletions} />}
                    />
                    <Route
                      path='setpoints-manager/:assetId/:assetGroupName'
                      element={<AuthComponent component={SetPointsManager} />}
                    />
                    <Route
                      path='installs/:assetId/:assetGroupName'
                      element={<AuthComponent component={Installation} />}
                    />
                    <Route
                      path='diagnosis/:assetId/:assetGroupName'
                      element={<AuthComponent component={WellDiagnosis} />}
                    />
                  </Route>
                  <Route path='/glDashboard' element={<AuthComponent component={GLDashboard} />} />
                  <Route path='/runlifeDashboard' element={<AuthComponent component={RunlifeDashboard} />} />
                  <Route path='/realtime-insights' element={<AuthComponent component={RealtimeInsights} />} />
                  <Route path='/group/status/:groupName' element={<AuthComponent component={GroupStatus} />} />
                  <Route path='/group/customViews/:groupName?' element={<AuthComponent component={CustomViews} />} />
                  <Route path='/production/well-location' element={<AuthComponent component={WellLocations} />} />
                  <Route path='/recommendations' element={<AuthComponent component={Recommendations} />} />
                  {/* <Route path='/defaultview' element={<AuthComponent component={DefaultView} />} /> */}
                </Route>
                <Route path='/onboarding' element={<AuthComponent component={OnboardingShell} />} />
                <Route path='/servererror' element={<ServerError />} />
                <Route path='/adloginerror' element={<ADLoginError />} />
              </Routes>
            </div>
            <TooltipManager />
          </AnalyticsListener>
        </PermissionModalProvider>
      </ToastServiceProvider>
    </RbacProvider>
  );
};

export default App;
